import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faTrash, 
  faKey
} from '@fortawesome/free-solid-svg-icons';
import { usersAPI } from '../api/apiService';
import styles from './UserManagement.module.css';

const UserManagement = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  
  // New user form state
  const [newUserEmail, setNewUserEmail] = useState('');
  const [newUserPassword, setNewUserPassword] = useState('');
  const [isCreatingUser, setIsCreatingUser] = useState(false);
  
  // Reset password state
  const [userToResetPassword, setUserToResetPassword] = useState(null);
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isResettingPassword, setIsResettingPassword] = useState(false);
  
  // Delete user state
  const [deleteConfirmation, setDeleteConfirmation] = useState('');
  const [userToDelete, setUserToDelete] = useState(null);
  
  // Fetch users when the component mounts
  useEffect(() => {
    fetchUsers();
  }, []);
  
  // Fetch the list of users
  const fetchUsers = async () => {
    try {
      setLoading(true);
      setError('');
      
      const response = await usersAPI.list();
      setUsers(response.users || []);
    } catch (err) {
      setError('Failed to load users. Please try again.');
      console.error('Error loading users:', err);
    } finally {
      setLoading(false);
    }
  };
  
  // Handle creating a new user
  const handleCreateUser = async (e) => {
    e.preventDefault();
    
    if (!newUserEmail || !newUserPassword) {
      setError('Email and password are required');
      return;
    }
    
    try {
      setIsCreatingUser(true);
      setError('');
      setSuccess('');
      
      const result = await usersAPI.create(newUserEmail, newUserPassword);
      
      setSuccess(`User ${newUserEmail} created successfully`);
      setNewUserEmail('');
      setNewUserPassword('');
      
      // Add the new user to the list without refreshing
      setUsers(prevUsers => [
        ...prevUsers,
        result.user
      ]);
    } catch (err) {
      setError(`Failed to create user: ${err.message}`);
      console.error('Error creating user:', err);
    } finally {
      setIsCreatingUser(false);
    }
  };
  
  // Handle opening password reset modal
  const handleResetPassword = (user) => {
    setUserToResetPassword(user);
    setNewPassword('');
    setConfirmPassword('');
  };

  // Confirm password reset
  const confirmResetPassword = async () => {
    if (!userToResetPassword) return;

    if (!newPassword || !confirmPassword) {
      setError('Password fields are required');
      return;
    }

    if (newPassword.length < 6) {
      setError('Password must be at least 6 characters');
      return;
    }

    if (newPassword !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    try {
      setIsResettingPassword(true);
      setError('');
      setSuccess('');
      
      await usersAPI.updatePassword(userToResetPassword.id, newPassword);
      
      setSuccess(`Password updated for ${userToResetPassword.email}`);
      setUserToResetPassword(null);
      setNewPassword('');
      setConfirmPassword('');
    } catch (err) {
      setError(`Failed to update password: ${err.message}`);
      console.error('Error updating password:', err);
    } finally {
      setIsResettingPassword(false);
    }
  };

  // Cancel password reset
  const cancelResetPassword = () => {
    setUserToResetPassword(null);
    setNewPassword('');
    setConfirmPassword('');
  };
  
  // Handle deleting a user
  const handleDeleteUser = async (user) => {
    setUserToDelete(user);
    setDeleteConfirmation('');
  };
  
  // Confirm user deletion
  const confirmDeleteUser = async () => {
    if (!userToDelete) return;
    
    if (deleteConfirmation !== userToDelete.email) {
      setError('Email confirmation does not match');
      return;
    }
    
    try {
      setError('');
      setSuccess('');
      
      await usersAPI.delete(userToDelete.id);
      
      setSuccess(`User ${userToDelete.email} deleted successfully`);
      
      // Remove the user from the list without refreshing
      setUsers(prevUsers => prevUsers.filter(user => user.id !== userToDelete.id));
      
      setUserToDelete(null);
      setDeleteConfirmation('');
    } catch (err) {
      setError(`Failed to delete user: ${err.message}`);
      console.error('Error deleting user:', err);
    }
  };
  
  // Cancel user deletion
  const cancelDeleteUser = () => {
    setUserToDelete(null);
    setDeleteConfirmation('');
  };
  
  // Render loading state
  if (loading && users.length === 0) {
    return (
      <div className={styles["user-management"]}>
        <div className={styles["loading-message"]}>Loading users...</div>
      </div>
    );
  }
  
  return (
    <div className={styles["user-management"]}>
      {error && <div className={styles["error-message"]}>{error}</div>}
      {success && <div className={styles["success-message"]}>{success}</div>}
      
      <div className={styles["user-management-content"]}>
        <div className={styles["user-list-section"]}>
          <h2>Users</h2>
          
          <table className={styles["user-table"]}>
            <thead>
              <tr>
                <th>Email</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {users.map((user) => (
                <tr key={user.id}>
                  <td>{user.email}</td>
                  <td>
                    <button 
                      className={`${styles["action-button"]} ${styles["reset-button"]}`}
                      onClick={() => handleResetPassword(user)}
                      disabled={isResettingPassword}
                      title="Set Password"
                    >
                      <FontAwesomeIcon icon={faKey} />
                    </button>
                    <button 
                      className={`${styles["action-button"]} ${styles["delete-button"]}`}
                      onClick={() => handleDeleteUser(user)}
                      title="Delete User"
                    >
                      <FontAwesomeIcon icon={faTrash} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          
          {users.length === 0 && !loading && (
            <div className={styles["no-users-message"]}>No users found.</div>
          )}
        </div>
        
        <div className={styles["user-form-section"]}>
          <h2>Add New User</h2>
          
          <form onSubmit={handleCreateUser} className={styles["user-form"]}>
            <div className={styles["form-group"]}>
              <label htmlFor="email">Email:</label>
              <input 
                type="email" 
                id="email"
                value={newUserEmail} 
                onChange={(e) => setNewUserEmail(e.target.value)}
                required
              />
            </div>
            
            <div className={styles["form-group"]}>
              <label htmlFor="password">Password:</label>
              <input 
                type="password" 
                id="password"
                value={newUserPassword} 
                onChange={(e) => setNewUserPassword(e.target.value)}
                required
              />
            </div>
            
            <button 
              type="submit" 
              className={styles["create-user-button"]}
              disabled={isCreatingUser}
            >
              {isCreatingUser ? 'Creating...' : 'Create User'}
            </button>
          </form>
        </div>
      </div>
      
      {userToDelete && (
        <div className={styles["delete-confirmation-modal"]}>
          <div className={styles["modal-content"]}>
            <h2>Confirm User Deletion</h2>
            
            <p>
              Are you sure you want to delete the user <strong>{userToDelete.email}</strong>?
              This action cannot be undone.
            </p>
            
            <div className={styles["confirmation-form"]}>
              <p>Type the user's email to confirm:</p>
              <input 
                type="text" 
                value={deleteConfirmation} 
                onChange={(e) => setDeleteConfirmation(e.target.value)}
                placeholder={userToDelete.email}
              />
            </div>
            
            <div className={styles["modal-actions"]}>
              <button 
                className={styles["cancel-button"]}
                onClick={cancelDeleteUser}
              >
                Cancel
              </button>
              <button 
                className={styles["confirm-button"]}
                onClick={confirmDeleteUser}
                disabled={deleteConfirmation !== userToDelete.email}
              >
                Delete User
              </button>
            </div>
          </div>
        </div>
      )}

      {userToResetPassword && (
        <div className={styles["delete-confirmation-modal"]}>
          <div className={styles["modal-content"]}>
            <h2>Set Password for {userToResetPassword.email}</h2>
            
            <div className={styles["confirmation-form"]}>
              <div className={styles["form-group"]}>
                <label htmlFor="newPassword">New Password:</label>
                <input 
                  type="password" 
                  id="newPassword"
                  value={newPassword} 
                  onChange={(e) => setNewPassword(e.target.value)}
                  placeholder="Enter new password"
                  minLength={6}
                />
              </div>
              
              <div className={styles["form-group"]}>
                <label htmlFor="confirmPassword">Confirm Password:</label>
                <input 
                  type="password" 
                  id="confirmPassword"
                  value={confirmPassword} 
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="Confirm new password"
                  minLength={6}
                />
              </div>
            </div>
            
            <div className={styles["modal-actions"]}>
              <button 
                className={styles["cancel-button"]}
                onClick={cancelResetPassword}
              >
                Cancel
              </button>
              <button 
                className={styles["confirm-button"]}
                onClick={confirmResetPassword}
                disabled={!newPassword || !confirmPassword || isResettingPassword}
              >
                {isResettingPassword ? 'Updating...' : 'Update Password'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserManagement;
